import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// Design-token chart palette (mirrors --chart-1..5 in index.css)
export const CHART_COLORS = [
  "hsl(214 95% 56%)", // chart-1 primary blue
  "hsl(142 71% 45%)", // chart-2 green
  "hsl(199 89% 48%)", // chart-3 sky
  "hsl(262 83% 58%)", // chart-4 violet
  "hsl(38 92% 50%)",  // chart-5 amber
];

const AXIS_STYLE = { fontSize: 11, fill: "hsl(215 16% 47%)" };
const TOOLTIP_STYLE = {
  borderRadius: 10, border: "1px solid hsl(214 32% 91%)", fontSize: 12,
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
};

/** Standard dashboard chart wrapper with loading / empty / error states. */
export const ChartCard = ({ title, icon: Icon, loading, error, empty, emptyText, onRetry, children, testId, className = "" }) => (
  <Card className={`rounded-xl border-border shadow-none ${className}`} data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-base font-heading flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />} {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="space-y-2" data-testid={testId ? `${testId}-loading` : undefined}>
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-4 w-1/3 rounded" />
        </div>
      ) : error ? (
        <div className="h-44 flex flex-col items-center justify-center gap-2 text-center" data-testid={testId ? `${testId}-error` : undefined}>
          <TriangleAlert className="h-6 w-6 text-amber-500" />
          <p className="text-sm text-muted-foreground">Could not load this chart.</p>
          {onRetry && (
            <Button size="sm" variant="outline" className="rounded-lg bg-white" onClick={onRetry}>Retry</Button>
          )}
        </div>
      ) : empty ? (
        <div className="h-44 flex items-center justify-center" data-testid={testId ? `${testId}-empty` : undefined}>
          <p className="text-sm text-muted-foreground">{emptyText || "No data for the selected filters."}</p>
        </div>
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

/** Vertical bar chart. bars: [{ key, label, color }] */
export const SimpleBarChart = ({ data, xKey, bars, height = 240, showLegend = false }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS_STYLE} tickLine={false} axisLine={false} interval="preserveStartEnd" />
      <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "hsl(214 32% 91% / 0.4)" }} />
      {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
      {bars.map((b, i) => (
        <Bar key={b.key} dataKey={b.key} name={b.label || b.key} fill={b.color || CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={38} />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

/** Horizontal funnel-style chart for the recruitment pipeline. */
export const FunnelBarChart = ({ data, nameKey = "stage", valueKey = "count", height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 32, left: 8, bottom: 4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" horizontal={false} />
      <XAxis type="number" tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
      <YAxis type="category" dataKey={nameKey} width={120} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "hsl(214 32% 91% / 0.4)" }} />
      <Bar dataKey={valueKey} name="Applications" radius={[0, 4, 4, 0]} maxBarSize={20}>
        <LabelList dataKey={valueKey} position="right" style={{ fontSize: 11, fill: "hsl(215 16% 47%)" }} />
        {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);
